import cx from 'classnames'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useDebounceCallback } from 'usehooks-ts'

import { LanguageContext } from '@lib/language'
import { PrintShopFormContext } from '@lib/print/print-shop-form-context'
import { PrintShopFormStringsContext } from '@lib/print/print-shop-form-strings-context'
import { updateShopPrint } from '@lib/print/request'
import { getPrintItemTooltipText } from '@lib/print/text'
import { PrintItemMode, ShopPrint } from '@lib/print/types'

import Icon from '@components/icon'
import InputField from '@components/input-field/with-managed-state'
import Photo from '@components/photo'
import Tooltip from '@components/tooltip'

interface ShopFormPrintItemProps {
  mode: PrintItemMode
  print: ShopPrint
  disabled?: boolean
  onRemove?: (printId: string) => Promise<void>
  className?: string
}

const ShopFormPrintItem = ({
  mode,
  print,
  disabled,
  onRemove,
  className,
}: ShopFormPrintItemProps) => {
  const { locale } = useContext(LanguageContext)
  const { shopState, dispatchShopState } = useContext(PrintShopFormContext)
  const { shopFormStrings } = useContext(PrintShopFormStringsContext)

  const [title, setTitle] = useState(print.title ?? '')

  const tooltipText = useMemo(() => {
    return getPrintItemTooltipText(
      shopFormStrings,
      print.printColor,
      print.isOptimized,
    )
  }, [print, shopFormStrings])

  const updatePrintTitle = useCallback(
    (shopId: string, printId: string, title: string) => {
      try {
        updateShopPrint(locale, shopId, printId, title)
      } catch (error) {
        console.error(error)
      }
    },
    [locale],
  )
  const debouncedUpdatePrintTitle = useDebounceCallback(updatePrintTitle, 600)

  const handleRemove = useCallback(() => {
    if (disabled) {
      return
    }

    if (onRemove) {
      onRemove(print.id)
    }
  }, [disabled, onRemove, print])

  const handleTitleChange = useCallback(
    async (newTitle: string) => {
      if (disabled || newTitle === title) {
        return
      }

      dispatchShopState({
        action: 'updatePrintTitle',
        id: print.id,
        title: newTitle,
      })

      if (shopState.id) {
        debouncedUpdatePrintTitle(shopState.id, print.id, newTitle)
      }
    },
    [
      debouncedUpdatePrintTitle,
      disabled,
      dispatchShopState,
      print.id,
      shopState.id,
      title,
    ],
  )

  const containerWidth = 60
  const containerHeight = 60
  const aspectRatio = print.image?.dimensions?.aspectRatio
  const imageWidth = aspectRatio
    ? Math.min(containerWidth, containerHeight * aspectRatio)
    : containerWidth
  const imageHeight = aspectRatio
    ? Math.min(containerHeight, containerWidth / aspectRatio)
    : containerHeight

  return (
    <div
      className={cx(
        'flex flex-col gap-2.5 p-5 border border-gray-light rounded-md bg-gray-lightest',
        {
          'opacity-50': disabled,
        },
        className,
      )}
    >
      <div className="flex justify-between gap-1">
        <div className="flex items-center gap-1">
          <p className="block leading-tight text-base font-semibold">
            {print.title ? print.title : print.filename}
          </p>

          {!!tooltipText && (
            <Tooltip
              content={
                <div className="max-w-xs py-2 px-3 rounded bg-black bg-opacity-80">
                  <p className="m-0 leading-snug text-sm text-white">
                    {tooltipText}
                  </p>
                </div>
              }
              placement="top-start"
              trigger={['click', 'hover']}
            >
              <Icon name="Info" className="text-lg opacity-30" />
            </Tooltip>
          )}
        </div>

        {mode === 'edit' && (
          <button
            onClick={handleRemove}
            className="flex-none flex items-center gap-2"
          >
            {!!shopFormStrings.shopFormRemovePrintLink && (
              <span className="text-base font-medium text-red">
                {shopFormStrings.shopFormRemovePrintLink}
              </span>
            )}
            <Icon
              id={`remove-print-${print.id}`}
              name="Trash"
              className="text-xl text-red"
            />
          </button>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div
          className="flex justify-center items-center rounded bg-green overflow-hidden"
          style={{
            width: containerWidth,
            height: containerHeight,
          }}
        >
          <Photo
            image={print.image}
            forceWidth={imageWidth}
            forceHeight={imageHeight}
          />
        </div>
        <p className="text-base leading-snug">{print.filename}</p>
      </div>

      {mode === 'edit' && (
        <InputField
          type="text"
          value={title}
          setValue={setTitle}
          label={shopFormStrings.shopFormPrintTitleLabel}
          placeholder={shopFormStrings.shopFormPrintTitlePlaceholder}
          onChange={(event) => {
            handleTitleChange(event.target.value)
          }}
          onBlur={(event) => {
            handleTitleChange(event.target.value)
          }}
          hasCustomInputStyles
          inputWrapperClassName="py-2 px-4 border border-gray-light rounded bg-white"
          inputClassName="text-base leading-snug bg-white"
          labelClassName="mb-1 font-semibold"
        />
      )}
    </div>
  )
}

export default ShopFormPrintItem
